import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Link, 
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Button,
  useMediaQuery,
  ThemeProvider,
  CssBaseline
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import FormSubmissionHandler from '../../../components/forms/renderer/FormSubmissionHandler';
import createAghrbaTheme from '../../../theme';
import logo from '../../../assets/images/logo_512.png';
import trackingService from '../../../services/trackingService';
import trackingConfig from '../../../config/tracking.config';

/**
 * Public page for rendering and submitting shared forms with Aghrba branding and theme
 */
const PublicFormView = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formDetails, setFormDetails] = useState({
    title: '',
    description: ''
  });
  // Theme mode switching - commented out as requested
  // const [themeMode, setThemeMode] = useState(() => {
  //   // Try to get stored theme preference or use system preference
  //   const storedTheme = localStorage.getItem('aghrba-form-theme');
  //   if (storedTheme) return storedTheme;
  //   
  //   // Check system preference
  //   const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  //   return prefersDarkMode ? 'dark' : 'light';
  // });
  
  // Using default light theme
  const themeMode = 'light';
  
  // Create theme based on current mode
  const theme = createAghrbaTheme(themeMode);
  
  // Detect if on mobile
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // Initialize tracking on component mount
  useEffect(() => {
    // Initialize tracking services with IDs from config or window fallback
    if (trackingConfig.general.enabled) {
      // Try config first, then fall back to window config
      const metaPixelId = trackingConfig.meta.pixelId || window.AGHRBA_TRACKING?.META_PIXEL_ID;
      const gaId = trackingConfig.googleAnalytics.measurementId || window.AGHRBA_TRACKING?.GOOGLE_ANALYTICS_ID;
      
      if (metaPixelId) {
        trackingService.initializeMetaPixel(metaPixelId);
      }
      if (gaId) {
        trackingService.initializeGoogleAnalytics(gaId);
      }
    }
  }, []);
  
  // Track form view when form details are loaded
  useEffect(() => {
    if (formDetails.title && shareToken) {
      trackingService.trackFormView(shareToken, formDetails.title, {
        form_type: 'public',
        share_token: shareToken
      });
    }
  }, [formDetails, shareToken]);
  
  // Toggle between light and dark theme - commented out
  // const toggleThemeMode = () => {
  //   const newThemeMode = themeMode === 'light' ? 'dark' : 'light';
  //   setThemeMode(newThemeMode);
  //   localStorage.setItem('aghrba-form-theme', newThemeMode);
  // };
  
  // Handle successful submission
  const handleSubmitSuccess = (submission) => {
    if (!submission || !submission._id) {
      return; // Don't continue if submission is invalid
    }
    
    // Create the navigation state with stringified values for localStorage
    const navigationState = { 
      submissionId: submission._id,
      message: submission.form?.thankYouMessage,
      formTitle: formDetails.title,
      themeMode, // Pass theme mode to thank you page
      timestamp: Date.now() // Add timestamp to prevent stale data
    };
    
    // Store submission data in localStorage as a fallback
    try {
      localStorage.setItem('form_submission_data', JSON.stringify(navigationState));
      localStorage.setItem('form_submission_timestamp', Date.now().toString());
    } catch (err) {
      // Silent error handling
    }
    
    // Use React Router navigation with state instead of window.location
    // This provides a smoother transition and preserves the state
    navigate(`/forms/shared/${shareToken}/thank-you`, { 
      state: navigationState,
      replace: true // Use replace instead of push to prevent back-button issues
    });
  };
  
  // Handle submission error
  const handleSubmitError = (error) => {
    // Error is handled by FormSubmissionHandler internally
  };
  
  // Handle form load to capture form details
  const handleFormLoad = (formData) => {
    if (formData) {
      setFormDetails({
        title: formData.title || 'Online Form',
        description: formData.description || ''
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f7',
          backgroundImage: 'linear-gradient(180deg, #f5f5f7 0%, #ffffff 100%)',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Header with branding and theme toggle */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.1)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative'
                }}
              >
                <img 
                  src={logo} 
                  alt="Aghrba Logo" 
                  style={{ 
                    height: 40, 
                    marginRight: 16
                  }} 
                />
              </Box>
              {/* Removed company name text to keep only the logo */}
            </Box>
            
            {/* Theme toggle - commented out as requested */}
            {/* <Tooltip title={`Switch to ${theme.palette.mode === 'dark' ? 'light' : 'dark'} mode`}>
              <IconButton 
                onClick={toggleThemeMode} 
                color={theme.palette.mode === 'dark' ? 'default' : 'primary'}
                aria-label="Toggle theme"
                size="medium"
              >
                {theme.palette.mode === 'dark' ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip> */}
          </Toolbar>
        </AppBar>
        
        {/* Main content - simplified layout */}
        <Box 
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            pt: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 3 }
          }}
        >
          <Container 
            maxWidth="md" 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%' 
            }}
          >
            {/* Simple loading overlay */}
            {submitting && (
              <Box 
                sx={{ 
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(4px)',
                  zIndex: 1000
                }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    // p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: 2,
                    maxWidth: '80%'
                  }}
                >
                  <CircularProgress 
                    size={48} 
                    sx={{ 
                      color: theme.palette.secondary.main
                    }}
                  />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Submitting your response...
                  </Typography>
                </Paper>
              </Box>
            )}
            
            {/* Elegant Form Container with integrated header */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                bgcolor: 'background.paper',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Elegant Header Section */}
              <Box 
                sx={{ 
                  p: { xs: 3, sm: 4, md: 5 },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(180deg, rgba(0, 123, 255, 0.04) 0%, rgba(255, 255, 255, 0) 100%)'
                }}
              >
                <Typography 
                  variant="h3" 
                  component="h1"
                  sx={{ 
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                    fontSize: { xs: '1.875rem', sm: '2.25rem', md: '2.5rem' }
                  }}
                >
                  {formDetails.title || 'Form'}
                </Typography>
                {formDetails.description && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      maxWidth: '80%',
                      fontSize: { xs: '0.95rem', sm: '1rem' }
                    }}
                  >
                    {formDetails.description}
                  </Typography>
                )}
              </Box>
              
              {/* Form Content */}
              <Box 
                sx={{ 
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FormSubmissionHandler
                  shareToken={shareToken}
                  onSubmitSuccess={handleSubmitSuccess}
                  onSubmitError={handleSubmitError}
                  setSubmitting={setSubmitting}
                  onFormLoaded={handleFormLoad}
                />
              </Box>
            </Paper>
            
            {/* Theme toggle - commented out as requested */}
            {/* <Box 
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                mt: 3
              }}
            >
              <Button 
                onClick={toggleThemeMode}
                startIcon={theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                variant="text"
                size="small"
                color="secondary"
                sx={{ textTransform: 'none' }}
              >
                {theme.palette.mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              </Button>
            </Box> */}
          </Container>
        </Box>
        
        {/* Main footer - outside the form card, at bottom of page */}
        <Box 
          component="footer"
          sx={{ 
            py: 2,
            mt: 'auto',
            borderTop: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.1)',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  position: 'relative',
                  mb: { xs: 1, sm: 0 }
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative'
                  }}
                >
                  <img 
                    src={logo} 
                    alt="Aghrba Logo" 
                    style={{ 
                      height: 24, 
                      marginRight: 8
                    }} 
                  />
                </Box>
                {/* Removed company name text to keep only the logo */}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-end' } }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <span 
                    style={{ 
                      color: theme.palette.primary.main,
                      fontWeight: '500'
                    }}
                  >
                    © {new Date().getFullYear()} All Rights Reserved
                  </span>
                  {/* Theme toggle link - commented out as requested */}
                  {/* <span>
                    <Link 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        toggleThemeMode();
                      }}
                      sx={{ 
                        color: theme.palette.secondary.main,
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </Link>
                  </span> */}
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicFormView;